import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Sparkles, ArrowLeft, ArrowRight, Share2, Copy, Check,
  Layers, ShieldCheck, Heart, MessageCircle, FileText,
  UserRound, Stethoscope, Clock, BrainCircuit, KeyRound,
} from 'lucide-react';

const SHARE_URL = 'https://docsite.care/whats-new/dhf-v3-conditions-dpdp';
const SHARE_MESSAGE = (
  `DocSite just made your Digital Health File smarter and safer.\n\n` +
  `• Each health issue gets its own clear section — no more guessing which tablet is for what\n` +
  `• Auto-warning if a new medicine clashes with your known allergies\n` +
  `• A short, plain-language summary shows up first, before you even open the file\n` +
  `• Your entire record is encrypted and locked to you — no one else can view, edit or use it. Ever.\n\n` +
  `Read more: ${SHARE_URL}`
);

/* ---------------- For Patients (addresses patients directly · "you" = patient) ------------------ */
const PATIENT_FEATURES = [
  {
    icon: Layers,
    title: 'Every health issue, on its own',
    body: 'When your doctor treats more than one thing in a single visit — say a throat infection and high blood pressure — the file now shows each condition on its own, with the medicines and tests grouped under it. No more guessing which tablet is for what.',
    color: 'sky',
  },
  {
    icon: Heart,
    title: 'Automatic allergy safety check',
    body: 'The file remembers the allergies you have told your doctor about. If a new prescription risks clashing with one of them, your doctor gets a warning before the file is even finalised. Safer prescriptions, fewer avoidable reactions.',
    color: 'rose',
  },
  {
    icon: MessageCircle,
    title: 'A friendly one-line summary at the top',
    body: 'Before you open the full file, you see a short plain-language line — what today\'s visit was really about and what to do next. Easy to read on WhatsApp, easy for family members to understand at a glance.',
    color: 'amber',
  },
  {
    icon: FileText,
    title: 'Clearer medicine labels',
    body: 'Each medicine now shows what type it is — tablet, capsule, syrup, spray, ointment and so on — with a friendly picture next to it. Less confusion at the pharmacy, especially for older parents and children reading the file on your behalf.',
    color: 'violet',
  },
];

/* ---------------- For Doctors (addresses doctors directly · "you" = doctor) ------------------ */
const DOCTOR_FEATURES = [
  {
    icon: Clock,
    title: 'Same great consult, in less time',
    body: 'The composer now naturally follows how you actually think in a consult — one health issue at a time, its own tests, its own follow-up. What used to take three minutes of clicking now takes under one.',
    color: 'sky',
  },
  {
    icon: BrainCircuit,
    title: 'Your patient\'s full history, waiting for you',
    body: 'When a patient returns, similar past visits from your practice surface automatically alongside the current one — what worked, how quickly they recovered, what to try differently. You look sharp because DocSite quietly remembered for you.',
    color: 'violet',
  },
  {
    icon: Stethoscope,
    title: 'A safety-net around every prescription',
    body: 'DocSite quietly cross-checks every new medicine you prescribe against the patient\'s allergy list and past prescriptions. If anything looks off, you see it before you finalise — not after the patient has walked home.',
    color: 'rose',
  },
  {
    icon: ShieldCheck,
    title: 'Compliance handled for you',
    body: 'Every file you generate is DPDP-2023 compliant out of the box — encrypted, consent-tracked, and audit-ready. You focus on care; DocSite handles the paperwork behind the paperwork.',
    color: 'emerald',
  },
];

const COLOR_MAP = {
  emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-200' },
  amber:   { bg: 'bg-amber-50',   icon: 'text-amber-600',   border: 'border-amber-200' },
  sky:     { bg: 'bg-sky-50',     icon: 'text-sky-600',     border: 'border-sky-200' },
  rose:    { bg: 'bg-rose-50',    icon: 'text-rose-600',    border: 'border-rose-200' },
  violet:  { bg: 'bg-violet-50',  icon: 'text-violet-600',  border: 'border-violet-200' },
};

function FeatureCard({ f }) {
  const c = COLOR_MAP[f.color] || COLOR_MAP.sky;
  const Ico = f.icon;
  return (
    <div className={`rounded-2xl bg-white border ${c.border} p-5 flex gap-4 items-start hover:shadow-sm transition-shadow`}>
      <div className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center shrink-0`}>
        <Ico size={20} className={c.icon} strokeWidth={1.75} />
      </div>
      <div className="min-w-0">
        <h3 className="text-base font-semibold text-slate-900 leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {f.title}
        </h3>
        <p className="text-sm text-slate-600 mt-1 leading-relaxed">{f.body}</p>
      </div>
    </div>
  );
}

export default function DhfV3ConditionsDpdpLaunch() {
  const [copied, setCopied] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(SHARE_MESSAGE)}`;
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'DocSite — Smarter Health File', text: SHARE_MESSAGE, url: SHARE_URL }); }
      catch { /* cancelled */ }
    } else { copyLink(); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(SHARE_URL); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* clipboard denied */ }
  };

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="dhf-v3-launch-page">
      <Helmet>
        <title>A smarter, safer Digital Health File | DocSite</title>
        <meta name="description" content="Your DocSite Digital Health File is now clearer, safer, and locked entirely to you. Encrypted end-to-end. No one else can view, edit or use it. Ever." />
        <meta property="og:title" content="DocSite — a smarter, safer Digital Health File" />
        <meta property="og:description" content="Clearer prescriptions. Safer for you. Encrypted and locked to you — no one else can view, edit or use it." />
        <meta property="og:url" content={SHARE_URL} />
      </Helmet>

      <div className="max-w-3xl mx-auto px-5 py-10">
        <Link to="/whats-new" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 mb-6" data-testid="back-to-whats-new">
          <ArrowLeft size={12} /> All updates
        </Link>

        {/* HERO */}
        <div className="rounded-3xl bg-gradient-to-br from-sky-50 via-white to-violet-50 border border-sky-200 p-6 sm:p-10 mb-6">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-sky-600 text-white">
            <Sparkles size={10} /> Just Launched · 1 Aug 2026
          </span>
          <div className="mt-5 flex items-center gap-4">
            <div className="relative shrink-0">
              <span className="absolute inset-0 rounded-2xl bg-sky-400/25 animate-ping" aria-hidden="true"></span>
              <span className="relative inline-flex w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-sky-600 via-violet-600 to-amber-500 shadow-lg items-center justify-center" data-testid="dhf-v3-icon">
                <Layers size={36} className="text-white sm:hidden" strokeWidth={2} />
                <Layers size={44} className="text-white hidden sm:block" strokeWidth={2} />
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-sky-700/80">Digital Health File · What&apos;s new</p>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Clearer prescriptions · Safer for you · Locked to you only</p>
            </div>
          </div>
          <h1 className="text-2xl sm:text-4xl font-semibold text-slate-900 mt-5 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            A smarter, safer Digital Health File.
          </h1>
          <p className="mt-3 text-base sm:text-lg text-slate-700 leading-relaxed">
            The file your doctor shares with you after a visit just got a whole lot better.
            Each health issue is now clearly separated. Allergies are auto-checked against new prescriptions.
            A short plain-language summary helps your family understand at a glance.
            And your entire record is encrypted and locked to you — no one else can view, edit or use it. Ever.
          </p>
        </div>

        {/* ─────────── FOR PATIENTS (addresses patients directly) ─────────── */}
        <div className="mb-4 flex items-center gap-2 mt-2" data-testid="section-for-you">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-sky-200 text-sky-800 text-[11px] font-bold uppercase tracking-wider">
            <UserRound size={11} /> For Patients
          </span>
          <span className="text-xs text-slate-500">— what changes the next time you visit a DocSite doctor</span>
        </div>
        <div className="space-y-3">
          {PATIENT_FEATURES.map((f, idx) => (
            <div key={idx} data-testid={`patient-feature-${idx}`}>
              <FeatureCard f={f} />
            </div>
          ))}
        </div>

        {/* ─────────── SECURITY / PRIVACY re-positioned ─────────── */}
        <div className="mt-8 rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-sky-50 border-2 border-emerald-200 p-6 relative overflow-hidden" data-testid="section-privacy">
          <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-emerald-100 opacity-40" aria-hidden="true"></div>
          <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-sky-100 opacity-40" aria-hidden="true"></div>

          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-widest">
              <KeyRound size={11} /> Your Record, Your Command
            </div>
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-4 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Locked to you. Only you.
            </h2>
            <p className="mt-3 text-sm sm:text-base text-slate-700 leading-relaxed max-w-2xl">
              Your entire Digital Health File is <strong className="text-slate-900">encrypted and sealed to you</strong>. Not to your doctor, not to their clinic, not to DocSite, not to any partner, not to any advertiser.
              <br /><br />
              <strong className="text-slate-900">No one else can view it, edit it, use it, or abuse it.</strong> Every access needs your consent. Every share happens on your terms, for a duration you choose. You decide who sees it, when, and for how long — and you can revoke that decision in one tap.
              <br /><br />
              DocSite is built to be the safest place in India for your health record — designed to India&apos;s newest privacy law, and quietly going beyond it.
            </p>

            <div className="mt-5 grid sm:grid-cols-3 gap-2">
              <div className="rounded-xl bg-white/70 border border-emerald-100 px-3 py-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Encrypted end-to-end</p>
                <p className="text-xs text-slate-600 mt-0.5">At rest and in transit. Every field. Always.</p>
              </div>
              <div className="rounded-xl bg-white/70 border border-emerald-100 px-3 py-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Consent-first, always</p>
                <p className="text-xs text-slate-600 mt-0.5">Every share, every purpose, every time — your call.</p>
              </div>
              <div className="rounded-xl bg-white/70 border border-emerald-100 px-3 py-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">You&apos;re the boss</p>
                <p className="text-xs text-slate-600 mt-0.5">Revoke access any time. In one tap. No questions.</p>
              </div>
            </div>
          </div>
        </div>

        {/* ─────────── FOR DOCTORS (addresses doctors directly) ─────────── */}
        <div className="mt-8 mb-4 flex items-center gap-2" data-testid="section-for-doctor">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-violet-200 text-violet-800 text-[11px] font-bold uppercase tracking-wider">
            <Stethoscope size={11} /> For Doctors
          </span>
          <span className="text-xs text-slate-500">— what you&apos;ll feel from the very next patient you see</span>
        </div>
        <div className="space-y-3">
          {DOCTOR_FEATURES.map((f, idx) => (
            <div key={idx} data-testid={`doctor-feature-${idx}`}>
              <FeatureCard f={f} />
            </div>
          ))}
        </div>

        {/* Why it matters */}
        <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-5">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-2 flex items-center gap-1.5">
            <ShieldCheck size={12} /> Why it matters
          </p>
          <p className="text-sm text-slate-700 leading-relaxed">
            A prescription is only useful if it is <em>clear</em>, <em>safe</em> and <em>truly yours</em>.
            DocSite is quietly turning health records from paper-era &quot;look up later&quot; documents into living,
            easy-to-read guides that keep you safer every visit — while keeping the record locked to only you.
            <span className="font-semibold text-slate-900"> Clearer. Safer. Yours, and only yours.</span>
          </p>
        </div>

        {/* Share strip */}
        <div className="mt-8 rounded-2xl bg-slate-900 text-white p-5 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <p className="text-sm flex-1">Know someone who&apos;d benefit? Share this in one tap.</p>
          <div className="flex gap-2 flex-wrap">
            <a href={whatsappHref} target="_blank" rel="noreferrer"
               className="px-3.5 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold flex items-center gap-1.5"
               data-testid="share-whatsapp">
              <Share2 size={14} /> WhatsApp
            </a>
            <button onClick={nativeShare}
                    className="px-3.5 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold flex items-center gap-1.5"
                    data-testid="share-native">
              <Share2 size={14} /> Share
            </button>
            <button onClick={copyLink}
                    className="px-3.5 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold flex items-center gap-1.5"
                    data-testid="share-copy">
              {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link to="/whats-new" className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700 hover:text-sky-900">
            See all DocSite updates <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
